import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';

const DAYS = 7;
const DAY_LABEL_ROWS = new Set([0, 2, 4]); // Mon, Wed, Fri

function getEntryDurationMs(entry) {
  const value = Number(entry.timerTime || entry.timer_time || 0);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function getEntryStartMs(entry) {
  const raw = Number(entry.timer_start || entry.timerStart || 0);
  if (!Number.isFinite(raw) || raw <= 0) return null;
  // Some historical timer payloads already carry millisecond timestamps.
  // Normalize both shapes so older months do not get bucketed into the wrong year.
  return raw < 1000000000000 ? raw * 1000 : raw;
}

function getIntensity(minutes, maxMinutes) {
  if (!minutes || maxMinutes <= 0) return 0;
  const ratio = minutes / maxMinutes;
  if (ratio < 0.2) return 1;
  if (ratio < 0.4) return 2;
  if (ratio < 0.7) return 3;
  return 4;
}

function formatDuration(minutes) {
  if (!minutes) return '0h';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (!hours) return `${mins}m`;
  if (!mins) return `${hours}h`;
  return `${hours}h ${String(mins).padStart(2, '0')}m`;
}

function buildWindowSeries(entries = [], timezone, windowOffset = 0, windowMonths = 3) {
  const zone = timezone || moment.tz.guess();
  const currentMonth = moment.tz(zone).startOf('month');
  const windowEnd = currentMonth.clone().subtract(windowOffset, 'months');
  const windowStart = windowEnd.clone().subtract(windowMonths - 1, 'months').startOf('month');
  const actualWindowEnd = windowEnd.clone().endOf('month');
  const totalsByDay = new Map();

  (entries || []).forEach((entry) => {
    if (Number(entry.isStop) !== 1) return;

    const startMs = getEntryStartMs(entry);
    const durationMs = getEntryDurationMs(entry);
    if (!startMs || !durationMs) return;

    const start = moment.tz(startMs, zone);
    if (!start.isBetween(windowStart, actualWindowEnd, 'day', '[]')) return;

    const key = start.format('YYYY-MM-DD');
    totalsByDay.set(key, (totalsByDay.get(key) || 0) + durationMs);
  });

  const months = [];
  let maxMinutes = 0;

  for (let i = 0; i < windowMonths; i += 1) {
    const monthStart = windowStart.clone().add(i, 'months');
    const monthEnd = monthStart.clone().endOf('month');
    const gridStart = monthStart.clone().startOf('isoWeek');
    const gridEnd = monthEnd.clone().endOf('isoWeek');
    const days = [];
    const cursor = gridStart.clone();

    while (cursor.isSameOrBefore(gridEnd, 'day')) {
      const key = cursor.format('YYYY-MM-DD');
      const inMonth = cursor.isBetween(monthStart, monthEnd, 'day', '[]');
      const minutes = inMonth ? Math.round((totalsByDay.get(key) || 0) / 60000) : 0;

      if (minutes > maxMinutes) maxMinutes = minutes;

      days.push({ key, date: cursor.clone(), minutes, inMonth });
      cursor.add(1, 'day');
    }

    const weeks = [];
    for (let j = 0; j < days.length; j += DAYS) {
      weeks.push(days.slice(j, j + DAYS));
    }

    months.push({
      key: monthStart.format('YYYY-MM'),
      monthLabel: monthStart.format('MMM'),
      fullMonthLabel: monthStart.format('MMMM'),
      yearLabel: monthStart.format('YYYY'),
      weeks,
    });
  }

  return {
    months,
    maxMinutes,
    rangeLabel: `${months[0].fullMonthLabel} ${months[0].yearLabel} – ${months[months.length - 1].fullMonthLabel} ${months[months.length - 1].yearLabel}`,
  };
}

function calculateStreak(entries, timezone) {
  const zone = timezone || moment.tz.guess();
  const today = moment.tz(zone).startOf('day');
  const totalsByDay = new Map();

  (entries || []).forEach((entry) => {
    if (Number(entry.isStop) !== 1) return;
    const startMs = getEntryStartMs(entry);
    const durationMs = getEntryDurationMs(entry);
    if (!startMs || !durationMs) return;
    const key = moment.tz(startMs, zone).format('YYYY-MM-DD');
    totalsByDay.set(key, (totalsByDay.get(key) || 0) + durationMs);
  });

  let streak = 0;
  const cursor = today.clone();

  if (!(totalsByDay.get(cursor.format('YYYY-MM-DD')) > 0)) {
    cursor.subtract(1, 'day');
  }

  while ((totalsByDay.get(cursor.format('YYYY-MM-DD')) || 0) > 0 && streak < 400) {
    streak++;
    cursor.subtract(1, 'day');
  }

  return streak;
}

export default function HeatWidget({ entries = [], timezone, onDateSelect, selectedDateKey = null, windowMonths = 3 }) {
  useTranslation();
  const [windowOffset, setWindowOffset] = useState(0);

  React.useEffect(() => { setWindowOffset(0); }, [windowMonths]);

  const todayKey = useMemo(
    () => moment.tz(timezone || moment.tz.guess()).format('YYYY-MM-DD'),
    [timezone],
  );

  const { months, maxMinutes, rangeLabel } = useMemo(
    () => buildWindowSeries(entries, timezone, windowOffset, windowMonths),
    [entries, timezone, windowOffset, windowMonths],
  );

  const summary = useMemo(() => {
    const days = months.flatMap((m) => m.weeks.flat()).filter((d) => d.inMonth);
    const totalMinutes = days.reduce((s, d) => s + d.minutes, 0);
    const activeDays = days.filter((d) => d.minutes > 0).length;
    const peakMinutes = days.reduce((max, d) => Math.max(max, d.minutes), 0);
    const streak = calculateStreak(entries, timezone);
    return { totalMinutes, activeDays, peakMinutes, streak };
  }, [months, entries, timezone]);

  const canGoNext = windowOffset > 0;

  const LEVEL_LABELS = ['No activity', '< 2h', '2–4h', '4–6h', '6h+'];
  const windowLabel = `${windowMonths}-month rolling window`;

  return (
    <div className="dash-card heat-widget-card">
      <div className="dash-card-header heat-widget-header">
        <div>
          <h3 className="dash-card-title">{tr("Work Pattern")}</h3>
          <p className="dash-card-subtitle">{tr("Daily tracked activity —")} {windowLabel}</p>
        </div>
        <div className="heat-widget-meta">
          <span>{formatDuration(summary.totalMinutes)} {tr("tracked")}</span>
          <span>{summary.activeDays} {tr("active days")}</span>
          {summary.streak > 0 && (
            <span className="heat-meta-streak">{summary.streak} {tr("day streak")}</span>
          )}
          {summary.peakMinutes > 0 && (
            <span>{tr("Peak")} {formatDuration(summary.peakMinutes)}</span>
          )}
        </div>
      </div>

      <div className="dash-card-body">
        <div className="heat-widget heat-widget--quarter">
          <div className="heat-widget-toolbar">
            <div className="heat-widget-range">{rangeLabel}</div>
            <div className="heat-widget-nav">
              <button
                type="button"
                className="heat-nav-btn"
                onClick={() => setWindowOffset((prev) => prev + windowMonths)}
              >{tr("Previous")} </button>
              <button
                type="button"
                className="heat-nav-btn"
                onClick={() => setWindowOffset((prev) => Math.max(0, prev - windowMonths))}
                disabled={!canGoNext}
              >{tr("Next")} </button>
            </div>
          </div>

          <div className="heat-quarter-grid" style={{ '--heat-cols': windowMonths }}>
            {months.map((month) => (
              <section key={month.key} className="heat-month-card">
                <div className="heat-month-card__header">
                  <span className="heat-widget-month">{month.monthLabel}</span>
                  <span className="heat-widget-year-number">{month.yearLabel}</span>
                </div>

                <div className="heat-month-grid-shell">
                  <div className="heat-squares-wrap">
                    <div className="heat-squares-days">
                      {Array.from({ length: DAYS }).map((_, rowIndex) => (
                        <span key={rowIndex}>
                          {DAY_LABEL_ROWS.has(rowIndex)
                            ? moment().isoWeekday(rowIndex + 1).format('ddd')
                            : ''}
                        </span>
                      ))}
                    </div>

                    <div
                      className="heat-squares-grid"
                      role="img"
                      aria-label={`Daily work activity heatmap for ${month.fullMonthLabel} ${month.yearLabel}`}
                    >
                      {month.weeks.map((week, weekIndex) => (
                        <div key={`${month.key}-${weekIndex}`} className="heat-squares-column">
                          {week.map((day) => {
                            const intensity = day.inMonth ? getIntensity(day.minutes, maxMinutes) : 0;
                            const isToday = day.key === todayKey && day.inMonth;
                            const isSelected = selectedDateKey === day.key && day.inMonth;
                            const classes = [
                              'heat-square',
                              `heat-square--${intensity}`,
                              day.inMonth ? '' : 'heat-square--outside',
                              isToday ? 'heat-square--today' : '',
                              isSelected ? 'heat-square--selected' : '',
                            ].filter(Boolean).join(' ');

                            return (
                              <button
                                key={day.key}
                                type="button"
                                className={classes}
                                title={
                                  day.inMonth
                                    ? `${day.date.format('ddd, MMM D')} · ${formatDuration(day.minutes)}`
                                    : ''
                                }
                                aria-label={
                                  day.inMonth
                                    ? `${day.date.format('ddd, MMM D, YYYY')}: ${formatDuration(day.minutes)}`
                                    : `Outside ${month.fullMonthLabel} ${month.yearLabel}`
                                }
                                onClick={() => {
                                  if (day.inMonth && typeof onDateSelect === 'function') {
                                    onDateSelect(day.key);
                                  }
                                }}
                              >
                                {day.inMonth ? day.date.date() : ''}
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>

          <div className="heat-widget-legend">
            <span className="heat-legend-label">{tr("No activity")}</span>
            <div className="heat-widget-legend-squares">
              {[0, 1, 2, 3, 4].map((level) => (
                <span
                  key={level}
                  className={`heat-square heat-square--${level}`}
                  title={LEVEL_LABELS[level]}
                />
              ))}
            </div>
            <span className="heat-legend-label">{tr("Most active")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
